@if (config && data !== undefined) {
  <div
    class="table-wrapper text-base relative"
    [ngClass]="{ 'shadow-sm': !fullScreen, 'hidden': !visible, 'animate-fadein': config.serviceCallback }"
  >
    @if (loadingService.loading() && !isRefreshing) {
      <app-loading-overlay></app-loading-overlay>
    }

    <!-- Export Mode Banner -->
    @if (exportMode) {
      <div class="px-8 py-4 flex items-center border-b bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-900 text-blue-400 dark:text-blue-300 animate-fadeintop">
        <div class="flex-grow text-sm font-normal">
          Vælg de rækker du ønsker at eksportere, eller eksporter hele tabellen.
        </div>
        <button
          class="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded cursor-pointer whitespace-nowrap"
          (click)="exportCsv()"
        >
          {{ 'common.export' | translate }}
        </button>
        <div class="w-8 h-8 flex items-center justify-center ml-2">
          <a
            href="#"
            (click)="$event.preventDefault(); cancelExport()"
          >
            <lucide-angular [name]="x"></lucide-angular>
          </a>
        </div>
      </div>
    }

    <!-- Copy Mode Banner -->
    @if (copyMode) {
      <div class="px-8 py-4 flex items-center border-b bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-900 text-blue-400 dark:text-blue-300 animate-fadeintop">
        <div class="flex-grow text-sm font-normal">
          Vælg de rækker du ønsker at kopiere, eller kopiér hele tabellen.
        </div>
        <button
          class="bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-4 py-1.5 rounded cursor-pointer whitespace-nowrap"
          (click)="copyCsvToClipboard()"
        >
          {{ 'common.copy_to_clipboard' | translate }}
        </button>
        <div class="w-8 h-8 flex items-center justify-center ml-2">
          <a
            href="#"
            (click)="$event.preventDefault(); cancelCopy()"
          >
            <lucide-angular [name]="x"></lucide-angular>
          </a>
        </div>
      </div>
    }

    <!-- Table -->
    @let stickyHeader = !!(config.floatingHeader && preferencesService.floatingTableHeader());
    @let stickyHeaderOffset = stickyHeader && preferencesService.floatingHeader();
    <table
      class="bg-gray-100 dark:bg-dark-hover table-fixed w-full"
      [ngClass]="{
        'rounded-t-sm': !fullScreen && !exportMode && !copyMode,
        'border-collapse': !stickyHeader,
        'border-separate border-spacing-0': stickyHeader
      }"
    >
      <!-- Table Header -->
      @if (selectedColumns && selectedColumns.length > 0) {
        <thead
          [ngClass]="{
            'sticky z-10 bg-gray-100 dark:bg-dark-hover': stickyHeader,
            'top-0': stickyHeader && !stickyHeaderOffset,
            'top-[97px]': stickyHeaderOffset
          }"
        >
        <tr
          cdkDropList
          (cdkDropListDropped)="dropColumn($event)"
          [cdkDropListData]="selectedColumns"
          cdkDropListOrientation="horizontal"
        >
          @if (config.selectable || exportMode || copyMode) {
            <th
              class="px-3 border-slate-200 dark:border-dark-border w-12 max-w-12 not:first-of-type:border-l-transparent border cursor-pointer"
              [ngClass]="{ 'border-b !border-b-slate-200': stickyHeader }"
            >
              @if (config.selectableType === 'multiple' || !config.selectableType || exportMode || copyMode) {
                <input
                  type="checkbox"
                  [checked]="data && data.length > 0 && selectedElements.length === data.length"
                  [disabled]="data.length === 0"
                  (click)="selectAll()"
                >
              }
            </th>
          }
          @for (header of selectedColumns; track columnIndex; let columnIndex = $index) {
            <th
              class="bg-gray-100 dark:bg-dark-hover rounded-t-sm text-left font-normal text-primary dark:text-dark-text border-slate-200 dark:border-dark-border border not:first-of-type:border-l-transparent"
              [ngStyle]="{ 'width.px': header.width }"
              [ngClass]="{ 'border-b': !config.pagination || stickyHeader, 'rounded-t-sm': !fullScreen }"
              [attr.aria-sort]="sort && sort.column === header.key ? (sort && sort.direction && sort.direction === SortDir.asc ? 'ascending' : 'descending') : null"
              [appResizeColumn]="config.resize !== false"
              [columnId]="header.key"
              [tableId]="id"
              [tooltip]="(header.title || '-') | translate"
              tooltipPosition="top"
              cdkDrag
            >
              <a
                href="#"
                class="flex w-full items-center justify-between px-2 py-3"
                [ngClass]="{ 'cursor-default': header.sortable === false || !config.oData }"
                (click)="$event.preventDefault(); config.oData && header.sortable !== false ? sortColumn(header) : null"
              >
                <div class="overflow-hidden whitespace-nowrap text-ellipsis">
                  {{ (header.title || '') | translate }}
                </div>
                @if (header.sortable !== false && config && config.disableSorting !== false && config.oData) {
                  <div
                    [ngClass]="[(sort && sort.column === header.key) ? 'text-slate-500' : 'text-slate-300']"
                  >
                    @let sortIcon = sort && sort.column === header.key ? (sort && sort.direction === SortDir.asc ? arrowDownAZ : arrowUpAZ) : arrowDownUp;
                    <lucide-angular [size]="12" [name]="sortIcon"></lucide-angular>
                  </div>
                }
              </a>
            </th>
          }
        </tr>
        </thead>
      }
      <tbody
        class="bg-white dark:bg-dark-card"
      >
      <!-- Table Body -->
        @if (isRefreshing) {
          @for (s of skeletonRowCount; track $index) {
            <tr class="border-b border-slate-200 dark:border-dark-border">
              @if (config.selectable || exportMode || copyMode) {
                <td class="border border-slate-200 dark:border-dark-border w-12 max-w-12 px-3 py-3"></td>
              }
              @for (col of selectedColumns; track col.key) {
                <td class="border border-slate-200 dark:border-dark-border px-3 py-3">
                  <div class="h-3.5 rounded-full ec-shimmer" [style.width.%]="50 + ($index * 13 % 35)"></div>
                </td>
              }
            </tr>
          }
        } @else if (data && data.length > 0) {
          @if (config.serviceCallback) {
            @for (row of data; track trackByFn(i, row); let even = $even; let i = $index) {
              <ng-container [ngTemplateOutlet]="rows" [ngTemplateOutletContext]="{ even: even, row: row, rowIndex: i }"></ng-container>
            }
          } @else {
            @for (row of data.slice((currentPage - 1) * pageSize, currentPage * pageSize); track trackByFn(i, row); let even = $even; let i = $index) {
              <ng-container [ngTemplateOutlet]="rows" [ngTemplateOutletContext]="{ even: even, row: row, rowIndex: i }"></ng-container>
            }
          }
        }

        @if (!isRefreshing && data && data.length === 0) {
          <tr>
            <td
              class="text-center font-normal align-middle border-slate-200 dark:border-dark-border border py-2 text-secondary"
              [colSpan]="selectedColumns.length + (config.selectable || exportMode || copyMode ? 1 : 0)"
            >
              {{ (config.emptyMessage || 'table.noresults') | translate }}
            </td>
          </tr>
        }
      </tbody>
    </table>

    <!-- Pagination -->
    @if (config.pagination !== false) {
      <app-table-pagination
        [pageSize]="pageSize"
        [fullScreen]="fullScreen"
        [(currentPage)]="currentPage"
        (currentPageChange)="handlePageChange(); currentPageChange.emit($event)"
        [dataLength]="config.serviceCallback ? dataLength : data.length"
      >
        @if (config.refreshable && (config.serviceCallback || config.refresh)) {
          <button
            (click)="refresh()"
            [disabled]="loadingService.loading()"
            class="py-2 px-2 rounded-md bg-gray-100 dark:bg-dark-hover flex items-center justify-center disabled:bg-slate-50 disabled:text-slate-300 disabled:cursor-not-allowed"
            [tooltip]="refreshTooltip"
            tooltipPosition="top"
          >
            <lucide-icon
              size="16"
              [name]="refreshCw"
              [ngClass]="{
                'animate-spin-once': isRefreshSpinning,
                'animate-spin': loadingService.loading() && !isRefreshSpinning
              }"
            ></lucide-icon>
          </button>
        }
        @if (config.columnSelection) {
          <!-- Column Selector --->
          <app-table-column-selector
            [(selectedColumns)]="selectedColumns"
            [availableColumns]="config.columns"
            (selectedColumnsChange)="onColumnChange()"
            (resetSettings)="resetSettings()"
          ></app-table-column-selector>
        }
        @if (config.pageSizeSelection) {
          <app-table-pagesize
            [(pageSize)]="pageSize"
            (pageSizeChange)="handlePageSizeChange($event)"
            [pageSizeOptions]="config.pageSizeOptions"
          ></app-table-pagesize>
        }
      </app-table-pagination>
    }
  </div>
}

<ng-template #rows let-even="even" let-row="row" let-rowIndex="rowIndex">
  <tr
    #rowEl
    class="hover:bg-slate-100 dark:hover:bg-dark-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-inset"
    [ngClass]="{
      'bg-slate-50 dark:bg-dark-hover': !even,
      '!bg-blue-50 hover:!bg-blue-100 dark:!bg-blue-900/30 dark:hover:!bg-blue-900/50': rowIsSelected(row),
      'ring-2 ring-blue-400 ring-inset': config.keyboardNavigation !== false && focusedRowIndex === rowIndex
    }"
    [attr.tabindex]="config.keyboardNavigation !== false ? ((focusedRowIndex ?? 0) === rowIndex ? 0 : -1) : null"
    (mousedown)="selectRow($event, row); onRowKeyboardFocus(rowIndex)"
    (focus)="onRowKeyboardFocus(rowIndex)"
    [appContextMenu]="computedContextActions"
  >
    @if (config.selectable || exportMode || copyMode) {
      <td
        class="text-center align-middle border-slate-200 dark:border-dark-border first-of-type:border-l border-r border-b cursor-pointer"
      >
        <input
          type="checkbox"
          class="pointer-events-none"
          [checked]="rowIsSelected(row)"
        >
      </td>
    }
    @for (cell of selectedColumns; track cell; let columnIndex = $index) {
      <td class="text-primary dark:text-dark-text py-2 px-2 border-slate-200 dark:border-dark-border first-of-type:border-l truncate border-r border-b">
        <app-table-cell
          [config]="selectedColumns[columnIndex]"
          [data]="row"
        ></app-table-cell>
      </td>
    }
  </tr>
</ng-template>
